import React from 'react';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaEnvelope, FaPhone, FaFacebook, FaTwitter, FaLinkedin, FaInstagram } from 'react-icons/fa';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Card from '../components/ui/Card';

function ContactPage() {
  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-16">
      <div className="container mx-auto px-4 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <span className="text-brand-primary font-bold tracking-wider uppercase text-sm mb-2 block">Get in Touch</span>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-800 font-display">Contact Us</h1>
          <p className="text-lg text-slate-600 mt-4 max-w-2xl mx-auto">
            We'd love to hear from you. Whether you have a question about features, pricing, or just want to say hi, our team is ready to answer all your questions.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Contact Information & Map */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-8"
          >
            <Card className="p-8 border-none shadow-lg bg-gradient-to-br from-brand-primary to-brand-dark text-white relative overflow-hidden">
              {/* Decorative Circles */}
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
              <div className="absolute bottom-10 left-10 w-20 h-20 bg-white/10 rounded-full blur-2xl"></div>

              <h2 className="text-2xl font-bold mb-6 relative z-10">Contact Information</h2>
              <div className="space-y-6 relative z-10">
                <div className="flex items-start">
                  <div className="bg-white/20 p-3 rounded-lg mr-4 backdrop-blur-sm">
                    <FaMapMarkerAlt className="text-xl" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white/90">Our Office</h3>
                    <p className="text-white/80 mt-1">123 Green Road, Farmgate,<br />Dhaka, Bangladesh</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-white/20 p-3 rounded-lg mr-4 backdrop-blur-sm">
                    <FaEnvelope className="text-xl" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white/90">Email Us</h3>
                    <p className="text-white/80 mt-1">support@Service24.com</p>
                    <p className="text-white/80">info@Service24.com</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-white/20 p-3 rounded-lg mr-4 backdrop-blur-sm">
                    <FaPhone className="text-xl" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white/90">Call Us</h3>
                    <p className="text-white/80 mt-1">+880 1700-822626</p>
                    <p className="text-white/80">+880 1800-123456</p>
                  </div>
                </div>
              </div>

              <div className="mt-12 relative z-10">
                <h3 className="font-semibold text-white/90 mb-4">Follow Us</h3>
                <div className="flex space-x-4">
                  <a href="#" className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"><FaFacebook /></a>
                  <a href="#" className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"><FaTwitter /></a>
                  <a href="#" className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"><FaLinkedin /></a>
                  <a href="#" className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"><FaInstagram /></a>
                </div>
              </div>
            </Card>

            {/* Map */}
            <div className="h-64 rounded-2xl overflow-hidden shadow-md border border-slate-200">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3651.894228553965!2d90.38338331542044!3d23.75194339452658!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3755b8b087026b81%3A0x8fa563bbdd5904c2!2sGreen%20Road%2C%20Dhaka!5e0!3m2!1sen!2sbd!4v1635253587525!5m2!1sen!2sbd"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title="Google Maps"
                className="grayscale hover:grayscale-0 transition-all duration-500"
              ></iframe>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="p-8 shadow-xl border-slate-100">
              <h2 className="text-3xl font-bold text-slate-800 mb-2">Send us a Message</h2>
              <p className="text-slate-500 mb-8">Fill out the form below and we'll get back to you shortly.</p>
              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input label="First Name" placeholder="John" />
                  <Input label="Last Name" placeholder="Doe" />
                </div>
                <Input label="Email Address" type="email" placeholder="john@example.com" />
                <Input label="Phone Number" placeholder="+880 1xxx-xxxxxx" />

                <div>
                  <label className="block text-slate-700 font-medium mb-2">Message</label>
                  <textarea
                    rows="4"
                    placeholder="How can we help you?"
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all resize-none bg-slate-50 focus:bg-white"
                  ></textarea>
                </div>

                <Button variant="primary" fullWidth size="lg" className="mt-4 shadow-lg shadow-brand-primary/20">
                  Send Message
                </Button>
              </form>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default ContactPage;